import { type SDK_Button, strings } from "@quickpoint.me/shared";
import { type ReactElement, memo, useContext, useEffect, useRef } from "react";
import styled, { type DefaultTheme, ThemeProvider } from "styled-components";
import {
	elementAlign,
	opacityHelper,
	resolveColorChoice,
} from "../../lib/cssHelpers";
import { FilterColors, FilterFonts } from "../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { HostId, IsPreviewGenerator } from "../../state/ui";

import { useRecoilValue } from "recoil";
import type { ButtonProps } from "../../@types/viewerati";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import useAiAnimation from "../../lib/animations/aiAnimationHook";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";
import createTestId from "../../lib/createTestId";
import { mainCssRules } from "../../lib/cssTemplates";
import MarkDownToTag from "../../lib/markdown/MarkDown";
import modifierStyled from "../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../state/accessibility";
import { recordHistory } from "../../lib/storage";
import { OwnerName } from "../../state/selectors";
import { StackCfKey } from "../../state/stack";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import { PositionContext } from "../displayElements/DisplayElements";

const ButtonFilter = (theme: DefaultTheme): SDK_Button => {
	return theme.button;
};

export const StyledButton = styled.button<ButtonProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
 display: flex;
 visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  flex-wrap: wrap;
  ${(props) => elementAlign(props.theme.align, props.$isInFlexBox)};
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  cursor: pointer;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  z-index: 101; //clickable things have to be above the scroll trigger
  position: relative;
  /* a button is as wide as its label, not the slide */
  width: fit-content;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`};
  /* theme color fields hold palette CHOICES; unresolved they paint nothing */
  background: ${(props) =>
		resolveColorChoice(props.theme.background, props.$filterColors)};
  border-color: ${(props) =>
		resolveColorChoice(props.theme.borderColor, props.$filterColors)};
  ${(props) => `${modifierStyled("button:", props)}`}
  .inline-link {
    color: inherit !important;
    text-decoration: none !important;
  }
`;

const Button = ({
	atomId,
	pageIndex,
	type,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const elRef = useRef(null);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const fonts = useRecoilValue(FilterFonts);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const ownerName = useRecoilValue(OwnerName);
	const pageNumber = elementData.modifier?.["toPage:"];
	const pageSize = useRecoilValue(PageSizeAtom);
	const tapped = elementData.tapped;
	const stackCfKey = useRecoilValue(StackCfKey);
	const position = useContext(PositionContext);
	const hostId = useRecoilValue(HostId);

	useTappedAnimation({ elRef, tapped, atomId });
	useAiAnimation({ elRef, modifier: elementData.modifier });

	const content =
		typeof elementData.content === "string"
			? MarkDownToTag({
					text: elementData.content,
					ownerName,
					modifier: elementData.modifier,
					hostId,
					tapped,
				})
			: elementData.content;

	const baseFontSize = pageSize.baseFontSize;
	const backlink = elementData.modifier?.["backlink:"];
	const url = elementData.modifier?.["url:"];
	let linkTo: string | undefined;
	if (backlink) {
		linkTo = `/${ownerName}/${backlink}`;
	}

	if (url) {
		linkTo = `http://${url}`;
	}
	let linkBc: BroadcastChannel | null = null;
	useEffect(() => {
		linkBc = new BroadcastChannel(strings.broadcastChannel.viewer);

		return () => {
			linkBc?.close();
		};
	});

	const handleClick = (
		e: React.MouseEvent,
		linkTo?: string,
		pageNumber?: string,
	) => {
		e.preventDefault();
		e.stopPropagation();
		// is it's a number go to that page otherwise try and open it as a URL
		if (linkTo) {
			recordHistory({ entry: stackCfKey, goingTo: linkTo });
			window.open(linkTo, "_self");
		}
		if (pageNumber && typeof pageNumber === "string") {
			if (linkBc)
				linkBc.postMessage({
					action: strings.messages.goToPage,
					payload: Number.parseInt(pageNumber),
				});
		}
	};

	return (
		<ThemeProvider theme={ButtonFilter}>
			<StyledButton
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$disabled={false}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$isInFlexBox={isInFlexBox}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				aria-hidden={!elementData.tapped}
				className={`${strings.css.pageElement}  Button`}
				data-atom-id={atomId}
				data-testid={createTestId({ atomId, position, type, isPreviewer })}
				onClick={(e) => handleClick(e, linkTo, pageNumber)}
				ref={elRef}
			>
				{content}
			</StyledButton>
		</ThemeProvider>
	);
};

export default memo(Button);
